// Maps Django field names to Arabic display names
const FIELD_NAMES: Record<string, string> = {
  name: 'اسم المؤشر',
  description: 'الوصف',
  kpi_type: 'نوع المؤشر',
  unit: 'وحدة القياس',
  target_value: 'القيمة المستهدفة',
  worst_acceptable_value: 'القيمة القصوى المقبولة',
  frequency: 'دورية القياس',
  polarity: 'اتجاه المؤشر',
  weight: 'الوزن',
  classification: 'التصنيف',
  measurement_formula: 'معادلة القياس',
  measurement_start_date: 'تاريخ بداية القياس',
  data_source: 'مصدر البيانات',
  owner: 'مالك المؤشر',
  source_type: 'نوع المصدر',
  strategic_goal: 'الهدف الاستراتيجي',
  initiative: 'المبادرة',
  source_process: 'العملية',
  source_sub_process: 'العملية الفرعية',
  source_service: 'الخدمة',
  source_activity: 'النشاط',
  organizational_unit_goal: 'هدف الوحدة التنظيمية',
};

/**
 * Extracts a human-readable error message from Django REST Framework 400 responses.
 * DRF returns field-level errors as { field_name: ["error message"] }
 */
export function extractKpiErrorMessage(errorData: any): string {
  if (!errorData) return '';
  if (typeof errorData === 'string') return errorData;
  if (errorData.detail) return errorData.detail;
  if (errorData.non_field_errors) {
    return Array.isArray(errorData.non_field_errors)
      ? errorData.non_field_errors.join(', ')
      : errorData.non_field_errors;
  }

  // Field-level errors: { field: ["msg1"], field2: ["msg2"] }
  const fieldErrors: string[] = [];
  for (const [field, messages] of Object.entries(errorData)) {
    const label = FIELD_NAMES[field] || field;
    const msgs = Array.isArray(messages) ? messages.join(', ') : String(messages);
    fieldErrors.push(`${label}: ${msgs}`);
  }
  return fieldErrors.join(' | ');
}

/**
 * Required KPI fields config: { formField, label (Arabic) }
 * Used for frontend validation before submitting.
 * "startDate" variant is for screens using startDate, "measurementStartDate" for Process screens.
 */
interface KpiRequiredField {
  field: string;
  label: string;
}

const REQUIRED_FIELDS: KpiRequiredField[] = [
  { field: 'name', label: 'اسم المؤشر' },
  { field: 'description', label: 'وصف المؤشر' },
  { field: 'kpiType', label: 'نوع المؤشر' },
  { field: 'unit', label: 'وحدة القياس' },
  { field: 'targetValue', label: 'القيمة المستهدفة' },
  { field: 'frequency', label: 'دورية القياس' },
  { field: 'polarity', label: 'اتجاه المؤشر' },
  { field: 'formula', label: 'معادلة القياس' },
];

/**
 * Validates required fields on all KPIs before save.
 * Returns null if valid, or a combined error message string listing
 * all missing fields across all KPIs (one line per KPI with issues).
 * dateField: the field name for measurement start date (varies per screen).
 */
export function validateKpiRequiredFields(
  kpis: any[],
  t: (key: string, opts?: any) => string,
  dateField: string = 'startDate',
): string | null {
  const allErrors: string[] = [];

  for (let i = 0; i < kpis.length; i++) {
    const kpi = kpis[i];
    // Each KPI row that exists in the list must be complete. Previously we
    // silently skipped rows whose name was empty, which let users save the
    // parent entity while their unfilled KPI row was dropped on the floor.
    // Require name + every other field; users can remove a row they don't
    // actually want via the X button.
    const missingFields: string[] = [];
    const displayName = (kpi.name || '').trim() || `#${i + 1}`;

    for (const { field, label } of REQUIRED_FIELDS) {
      const val = kpi[field];
      if (!val || (typeof val === 'string' && !val.trim())) {
        missingFields.push(label);
      }
    }

    // Target value checks:
    //   - positive polarity: must be > 0
    //   - negative polarity: may be 0 (zero-target KPIs), but must be >= 0
    const targetNum = parseFloat(kpi.targetValue);
    const isNegative = kpi.polarity === 'negative';
    const isPercent = kpi.unit === '%';

    if (!isNaN(targetNum)) {
      if (!isNegative && targetNum <= 0) {
        missingFields.push('القيمة المستهدفة (يجب أن تكون أكبر من 0)');
      } else if (isNegative && targetNum < 0) {
        missingFields.push('القيمة المستهدفة (يجب أن تكون 0 أو أكبر)');
      }
    }

    // Worst-acceptable value checks for negative polarity + non-% units
    if (isNegative && !isPercent) {
      const worstStr = kpi.worstAcceptableValue;
      const worstNum = worstStr === '' || worstStr == null ? null : parseFloat(worstStr);

      if (targetNum === 0 && worstNum === null) {
        missingFields.push('القيمة القصوى المقبولة');
      } else if (worstNum !== null && !isNaN(worstNum) && !isNaN(targetNum) && worstNum <= targetNum) {
        missingFields.push('القيمة القصوى المقبولة (يجب أن تكون أكبر من القيمة المستهدفة)');
      }
    }

    // Check measurement start date
    const dateVal = kpi[dateField];
    if (!dateVal || (typeof dateVal === 'string' && !dateVal.trim())) {
      missingFields.push('تاريخ بداية القياس');
    }

    // DTS section 4.4 — an independent strategic KPI is unwritable
    // without implementing_section (the only section allowed to enter
    // readings). Catch it here so the form surfaces the requirement
    // alongside name/target/etc., instead of letting the user submit
    // and bounce off the API error.
    if (kpi.dataSourceType === 'independent') {
      const impl = kpi.implementingSection;
      if (!impl || (typeof impl === 'string' && !impl.trim())) {
        missingFields.push('القسم المنفذ');
      }
    }

    if (missingFields.length > 0) {
      allErrors.push(`${displayName}: ${missingFields.join('، ')}`);
    }
  }

  if (allErrors.length === 0) return null;
  return allErrors.join('\n');
}

/**
 * Validates that KPI measurement_start_date does not exceed the strategic goal's target_completion_date.
 * Returns null if valid, or an error message string.
 */
export function validateKpiMeasurementDate(
  kpis: any[],
  goalTargetDate: string,
  dateField: string = 'startDate',
): string | null {
  if (!goalTargetDate) return null;

  const errors: string[] = [];
  for (const kpi of kpis) {
    const name = kpi.name;
    if (!name || !name.trim()) continue;

    const dateVal = kpi[dateField];
    if (dateVal && dateVal > goalTargetDate) {
      errors.push(
        `${name}: تاريخ بداية القياس (${dateVal}) يجب ألا يتجاوز تاريخ الإنجاز المستهدف للهدف (${goalTargetDate})`
      );
    }
  }

  if (errors.length === 0) return null;
  return errors.join('\n');
}
